import { Fragment, useMemo } from "react"
import { Box, useTheme } from "@mui/material"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"

import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar"

import { SearchToggleButton } from "components/domains/search"
import { EmptyData } from "components/EmptyData"
import { VenueSelector } from "components/VenueSelector"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { Head } from "components/Head"
import { StoreIcon } from "components/icons"
import { MotionDiv, showContent } from "components/motions"
import { DirectorySkeletonCard } from "components/domains/directory"
import { LanguageButton } from "extensions/iconsiam/components/language/LanguageButton"
import { useDirectory } from "extensions/iconsiam/hooks/directory/useDirectory"
import { AnimatePresence } from "framer-motion"
import {
  DirectoryOccupantCard,
  SubCategorySidebar,
} from "extensions/iconsiam/components/domains/directory"
import { HorizontalScrollBarWithButton } from "extensions/iconsiam/components/HorizontalScrollBar/HorizontalScrollBarWithButton"
import {
  LTRSidebarVariant,
  LTRContentVariant,
  RTLSidebarVariant,
  RTLContentVariant,
} from "extensions/iconsiam/components/domains/motions/variants"
import { useElementScrollRestoration } from "hooks/scrollbar/useElementScrollRestoration"

export const DirectoryKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { t } = useTranslation()
  const { openSearch } = useAppUI()
  const {
    handleChangeParentCategory,
    handleChangeSubCategory,
    changeVenueFilter,
    filteredOccupantsSorted,
    sortedParentCategoryWithOccupant,
    subCategoryWithActiveParentCategory,
    parentCategoryId,
    subCategoryId,
    venueFilter,
    ref,
    endScrollRef,
    hasNextPage,
    showSubCategoryFilter,
    venues,
    isMultiVenueProject,
    hasData,
    hasParentCategory,
    parentCategory,
  } = useDirectory()

  const theme = useTheme()
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.directory) || <StoreIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`DirectoryPage`]?.styleOverrides || {}
  const {
    root,
    filterSection,
    gridContainerWrapper,
    gridContainer,
    gridItem,
    categoryFilterBar,
    subCategoryFilterBar,
    emptyDataWrapper,
    venueSelector,
    navigationWrapper,
    actionButton,
    actionContainer,
    toolbarContainer,
    searchBarContainer,
    actionWrapper,
    contentBox,
  } = styleOverrides

  const { scrollRef } = useElementScrollRestoration<HTMLDivElement>(
    "directory-page-scroll-pane"
  )

  const isRtl = theme.isRtl
  const sidebarVariants = isRtl ? RTLSidebarVariant : LTRSidebarVariant
  const contentVariants = isRtl ? RTLContentVariant : LTRContentVariant

  return (
    <>
      <Head title={t("Directory")} />
      <Layout>
        <Box ref={ref} sx={root}>
          <Box sx={contentBox}>
            <AnimatePresence mode="wait">
              {subCategoryWithActiveParentCategory.length > 0 && (
                <MotionDiv
                  key="sidebar"
                  variants={sidebarVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <SubCategorySidebar
                    mainCategory={parentCategory}
                    SubCategoryComponent={
                      <CategoryFilterBar
                        sx={subCategoryFilterBar}
                        activeCategory={subCategoryId}
                        categories={subCategoryWithActiveParentCategory}
                        onChange={handleChangeSubCategory}
                        renderFilterButton={CategoryFilterChip}
                      />
                    }
                  />
                </MotionDiv>
              )}
            </AnimatePresence>
            {hasData ? (
              <Box sx={gridContainerWrapper} ref={scrollRef}>
                <MotionDiv
                  layout="preserve-aspect"
                  layoutDependency={showSubCategoryFilter}
                  variants={contentVariants.kioskV}
                  initial="initial"
                  animate={showSubCategoryFilter ? "withSidebar" : "animate"}
                  sx={gridContainer}
                >
                  {filteredOccupantsSorted.pages.map((page, pageNum) => {
                    return (
                      <Fragment key={pageNum}>
                        {page.data.map((occupant) => {
                          const isAd = occupant?.properties?.isAd
                          const occupantId = occupant?.id
                          const isNewShop = occupant?.properties?.isNewShop
                          const isComingSoon =
                            occupant?.properties?.isComingSoon
                          return (
                            <Box
                              id={occupantId}
                              data-testid={occupantId}
                              sx={gridItem}
                              key={`${
                                isAd ? "ad-" : "directory-item"
                              }${occupantId}`}
                            >
                              <DirectoryOccupantCard
                                data={occupant}
                                isNewShop={isNewShop}
                                isComingSoon={!isAd && isComingSoon}
                              />
                            </Box>
                          )
                        })}
                      </Fragment>
                    )
                  })}
                  {hasNextPage &&
                    Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                      <DirectorySkeletonCard
                        ref={
                          index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                        }
                        key={index}
                        sx={gridItem}
                      />
                    ))}
                </MotionDiv>
              </Box>
            ) : (
              <MotionDiv
                sx={emptyDataWrapper}
                variants={showContent}
                initial="initial"
                animate="animate"
              >
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Stay tuned for the update")}
                />
              </MotionDiv>
            )}
          </Box>
          <Box key="app-navigation" sx={navigationWrapper}>
            <Box sx={actionWrapper}>
              {hasParentCategory && (
                <Box sx={filterSection}>
                  <HorizontalScrollBarWithButton className="category-filterbar">
                    <CategoryFilterBar
                      sx={categoryFilterBar}
                      activeCategory={parentCategoryId}
                      categories={sortedParentCategoryWithOccupant}
                      onChange={handleChangeParentCategory}
                    />
                  </HorizontalScrollBarWithButton>
                </Box>
              )}
              <Box sx={toolbarContainer}>
                <Box sx={actionContainer}>
                  <LanguageButton sx={actionButton} />
                  {additionalMenu}
                  <Box sx={searchBarContainer}>
                    <SearchToggleButton
                      placeholder={t("Search shops and more...")}
                      onClick={openSearch}
                    />
                  </Box>
                </Box>
              </Box>
              {isMultiVenueProject && (
                <Box sx={venueSelector}>
                  <VenueSelector
                    selectedVenue={venueFilter}
                    venues={venues}
                    onClickVenue={changeVenueFilter}
                  />
                </Box>
              )}
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
    </>
  )
}
